// The artifacts, as they cross the wire (ICD-S10, DATA_LAYER_DESIGN §4.4).
//
// An artifact is a document that is the same for every league of a sport,
// built from the record by the ingest run that changed its inputs, named by a
// version and never modified. `GET /api/sports/:sport/artifacts` names the
// current version of each kind; `GET /api/artifacts/:sport/:kind/:version`
// is the document, which never changes and is cached as such. The machinery
// — the store, the pointer row, the reader — is `@fsp/artifacts`; these are
// only the shapes, here with every other served shape so a client generates
// them from `docs/design/contracts/schema/` like the rest.
//
// NOTHING IN ONE IS PRIVATE, and nothing in one is a vendor's row passed
// through: the directory is `/api/players`' own shape, which is public.

import { z } from 'zod';
import { DirectoryPlayer } from '../providers/directory';

const Iso = z.string();

/**
 * A version: the first sixteen hex digits of the SHA-256 of the document
 * without it. The same content is the same version, so a key is never written
 * twice with different bytes.
 */
export const ArtifactVersion = z.string().regex(/^[0-9a-f]{16}$/);

/**
 * A week's kind: `window-` and the lineup window's id (`window-2026-w06`).
 * One document per window, since a screen asks about one week at a time.
 */
export const WindowKind = z.string().regex(/^window-[A-Za-z0-9._:-]{1,64}$/);

/** A season's kind: `lines-` and the season (`lines-2025`). */
export const LinesKind = z.string().regex(/^lines-\d{4}$/);

/** A finished season's stat history, as the projection job reads it: `history-` and the season. */
export const HistoryKind = z.string().regex(/^history-\d{4}$/);

/** A season's games: `schedule-` and the season (`schedule-2026`). */
export const ScheduleKind = z.string().regex(/^schedule-\d{4}$/);

/** A season's usage: `usage-` and the season (`usage-2026`). */
export const UsageKind = z.string().regex(/^usage-\d{4}$/);

/** The kinds served today. A kind is added here with its document. */
export const ArtifactKind = z.union([
  z.literal('directory'),
  WindowKind,
  LinesKind,
  HistoryKind,
  ScheduleKind,
  UsageKind,
]);
export type ArtifactKind = z.infer<typeof ArtifactKind>;

/** What a kind's document is, whatever it is about: every `window-…` is a `window`. */
export type ArtifactFamily = 'directory' | 'window' | 'lines' | 'history' | 'schedule' | 'usage';

/** A kind's family, or null for a kind this build does not know. */
export function artifactFamily(kind: string): ArtifactFamily | null {
  if (kind === 'directory') return 'directory';
  if (WindowKind.safeParse(kind).success) return 'window';
  if (LinesKind.safeParse(kind).success) return 'lines';
  if (ScheduleKind.safeParse(kind).success) return 'schedule';
  if (UsageKind.safeParse(kind).success) return 'usage';
  return HistoryKind.safeParse(kind).success ? 'history' : null;
}

/** The kind of a season's lines. */
export const linesKind = (season: number): string => `lines-${season}`;

/** The kind of a finished season's stat history. */
export const historyKind = (season: number): string => `history-${season}`;

/** The kind of a season's games. */
export const scheduleKind = (season: number): string => `schedule-${season}`;

/** The kind of a season's usage. */
export const usageKind = (season: number): string => `usage-${season}`;

/** The kind of a lineup window's projections. */
export const windowKind = (windowId: string): string => `window-${windowId}`;

/**
 * Each family's shape, as a number. RAISED WITH ANY CHANGE TO A FAMILY'S
 * DOCUMENT: the pointer row carries the number its version was built under, a
 * reader treats a version of another shape as absent (it reads the record),
 * and the next ingest pass rebuilds it — so a deploy that changes a shape
 * never leaves the screens on a document they cannot parse, or on the record
 * for days.
 */
export const ARTIFACT_SHAPES: Record<ArtifactFamily, number> = {
  // 2 since 2026-10-10: each entry says whether it is a club unit (`club`).
  // 3 since 2026-10-22: each entry carries its depth chart place (`depth`).
  // 4 since 2026-10-27: each entry says where the pro draft took him (`proDraftPick`).
  // 5 since 2026-11-01: an entry's designation says when its report was read (`injury.readAt`).
  directory: 5,
  window: 1,
  lines: 1,
  history: 1,
  schedule: 1,
  usage: 1,
};

/** What every document says about itself. */
export const ArtifactHeader = z.object({
  sport: z.string().min(1),
  kind: ArtifactKind,
  version: ArtifactVersion,
  /** When the build that made it ran. */
  builtAt: Iso,
  /** The run ledger's row for that build (`ingest_runs.id`). */
  runId: z.string().min(1),
  /** The newest moment of the record it reflects; null when it reflects nothing dated. */
  asOf: Iso.nullable(),
});
export type ArtifactHeader = z.infer<typeof ArtifactHeader>;

/** One player of the directory artifact: `/api/players`' row and two things the league pool reads. */
export const DirectoryEntry = DirectoryPlayer.extend({
  /**
   * In the simulator's world rather than the real one: the row carries a
   * `sim` or `sim-team` key, which is `worldClause`'s test. A league plays
   * one world or the other.
   */
  simulated: z.boolean(),
  /** The injury note as the table holds it, which can stand without a designation; `injury` drops it then. */
  injuryNote: z.string().nullable(),
  /**
   * His latest practice report in our own words, as the table holds it
   * (`players.practice_status`), or null with none: what a bot weighs a
   * Questionable player by (B-503). A document built before 2026-10-25 has
   * none, and reads as no report.
   */
  practice: z.string().nullable().default(null),
  /**
   * A club unit — `players.kind = 'club'`, scored from the club's own line
   * rather than a person's — not a person. The league screens' week reads ask
   * it of every player in the week's lineups (`clubUnits`), and since
   * 2026-10-10 ask the document rather than the table (PLAN-2026-10-10 chunk 5).
   */
  club: z.boolean(),
  /**
   * The overall pick the pro league drafted him with, 1 the first, or null
   * for a player nobody drafted (`players.pro_draft_pick`, 0087): where the
   * house ranking puts a player it has no season for. The directory's shape
   * is 4 with it, so a document built before it is rebuilt rather than read.
   */
  proDraftPick: z.number().int().nullable().default(null),
});
export type DirectoryEntry = z.infer<typeof DirectoryEntry>;

/**
 * A directory entry as `/api/players` serves it: the wire's row, without
 * what only the league pool and the bots read. ONE COPY, for the endpoint and
 * for the suite that holds the document to the table, so a field the entry
 * gains cannot reach the wire through one of them (it did, 2026-10-25).
 */
export function directoryPlayerOf(entry: DirectoryEntry): DirectoryPlayer {
  const {
    simulated: _s,
    injuryNote: _n,
    practice: _p,
    club: _c,
    proDraftPick: _d,
    ...player
  } = entry;
  return player;
}

/** `directory`: every player of the sport, every status, in `compareDirectory` order. */
export const DirectoryDocument = ArtifactHeader.extend({
  kind: z.literal('directory'),
  players: z.array(DirectoryEntry),
});
export type DirectoryDocument = z.infer<typeof DirectoryDocument>;

/**
 * One house projection of a week: the stored line (null when it was
 * withheld) and what it says about itself, as `player_projections` holds them
 * and as every reader of that table parses them — a row whose line or basis
 * would not parse there is not here either.
 */
export const WindowProjection = z.object({
  playerId: z.string(),
  /** Stat key to projected value; null when the projection was withheld, and the basis says why. */
  line: z.record(z.string(), z.number()).nullable(),
  /** The engine, the game it was for, the games it rests on, a zero's or a withholding's reason (ICD-P07). */
  basis: z.record(z.string(), z.unknown()),
});
export type WindowProjection = z.infer<typeof WindowProjection>;

/** `window-<id>`: every house projection of one lineup window, for every player of the sport. */
export const WindowDocument = ArtifactHeader.extend({
  kind: WindowKind,
  /** The season the window is in, which the projections are keyed by beside it. */
  season: z.number().int(),
  windowId: z.string().min(1),
  projections: z.array(WindowProjection),
});
export type WindowDocument = z.infer<typeof WindowDocument>;

/**
 * One stat line of a season, as `player_period_stats` holds it: every version,
 * so a reader can take a settled week's line as it stood when the week was
 * scored (`lineAsOf`) as well as the current one. The real world's lines
 * only; the simulator's are the seeder's, and a seeded league reads the table.
 */
export const SeasonLine = z.object({
  playerId: z.string(),
  /** The lineup window the line counts in (`LineupWindow.id`). */
  windowId: z.string(),
  gameId: z.string(),
  /** 1, then one more for each correction. */
  version: z.number().int().min(1),
  /** The version in force now: exactly one per player and game. */
  current: z.boolean(),
  /** When this version was written: what "as it stood at" compares. */
  updatedAt: Iso,
  /** Stat key to value. */
  line: z.record(z.string(), z.number()),
});
export type SeasonLine = z.infer<typeof SeasonLine>;

/** `lines-<season>`: every stat line of one season, every version, for every player of the sport. */
export const LinesDocument = ArtifactHeader.extend({
  kind: LinesKind,
  season: z.number().int(),
  lines: z.array(SeasonLine),
});
export type LinesDocument = z.infer<typeof LinesDocument>;

/**
 * What the house projection engine reads off a stat line: the stat fact's
 * payload (`stat.line`, or a club's `team.stat.line`) with only these
 * fields kept. The fact registry owns the full shape.
 */
export const HistoryLine = z.object({
  season: z.number().int(),
  period: z.number().int(),
  seasonType: z.string().nullable().optional(),
  position: z.string().nullable().optional(),
  team: z.string().nullable().optional(),
  opponent: z.string().nullable().optional(),
  /** A club's line names its club here instead of `team`. */
  teamKey: z.string().optional(),
  gameId: z.string().nullable().optional(),
  stats: z.record(z.string(), z.number().finite()),
});
export type HistoryLine = z.infer<typeof HistoryLine>;

/** One current stat line of a finished season, as the projection job reads it. */
export const HistoryRow = z.object({
  /**
   * Who the line is about, by the id `/api/players` uses — and whoever else
   * the record has a line for, in the directory or not, so the document does
   * not change when the directory does: the job keeps the sport's players.
   */
  playerId: z.string(),
  /** A club's line (`team.stat.line`), whose points allowed the job joins from the final score. */
  club: z.boolean(),
  /** The week the record files it under, which orders the history. */
  period: z.number().int(),
  /** Null when the stored payload no longer parses: the job counts it and reads past it. */
  line: HistoryLine.nullable(),
});
export type HistoryRow = z.infer<typeof HistoryRow>;

/**
 * `history-<season>`: every current stat line of one finished season, in the
 * order the projection job reads them — a person's lines by week, then a
 * club's. The real world's only.
 */
export const HistoryDocument = ArtifactHeader.extend({
  kind: HistoryKind,
  season: z.number().int(),
  rows: z.array(HistoryRow),
});
export type HistoryDocument = z.infer<typeof HistoryDocument>;

/**
 * One game of a season, as `games` holds it — the real world's only: the
 * simulator's are the seeder's world, and a seeded league reads the table.
 */
export const ScheduleGame = z.object({
  /** `<sport>:<the provider's game id>`, as every league table names it. */
  id: z.string(),
  /** Kickoff; empty while the provider has not set one, as the table holds it. */
  startAt: z.string(),
  home: z.string(),
  away: z.string(),
  /** As the table holds it: `scheduled`, or `final` once the result is in. */
  status: z.string(),
  /** The week the provider files it under; null when it names none. */
  periodHint: z.number().int().nullable(),
  /** The final score, once there is one. */
  homeScore: z.number().int().nullable(),
  awayScore: z.number().int().nullable(),
  /** The source that scheduled it (`games.source`); null for a row written before the column. */
  source: z.string().nullable(),
});
export type ScheduleGame = z.infer<typeof ScheduleGame>;

/** `schedule-<season>`: every game of one season in the real world, by kickoff and then id. */
export const ScheduleDocument = ArtifactHeader.extend({
  kind: ScheduleKind,
  season: z.number().int(),
  games: z.array(ScheduleGame),
});
export type ScheduleDocument = z.infer<typeof ScheduleDocument>;

/**
 * One player's usage (SCENARIOS E8): his share of his clubs' totals of the
 * sport's usage stat over the season's games — the club each line was filed
 * for — and over the last three of them, and its partner stat a game.
 */
export const PlayerUsage = z.object({
  playerId: z.string(),
  /** Games with a line whose club is known. */
  games: z.number().int().min(1),
  /** His share of his clubs' totals of the `share` stat over those games, 0 to 1. */
  share: z.number().min(0).max(1),
  /** The same over the last three of them. */
  shareLastThree: z.number().min(0).max(1),
  /** The `perGame` stat a game over those games; null where no line of the season measures it. */
  perGame: z.number().nullable(),
});
export type PlayerUsage = z.infer<typeof PlayerUsage>;

/**
 * `usage-<season>`: every player's usage over one season in the real world,
 * in player order. A player with none of the stat in a game whose club is
 * known is not here. The simulator's world is the seeder's, and a seeded
 * league reads the kept table.
 */
export const UsageDocument = ArtifactHeader.extend({
  kind: UsageKind,
  season: z.number().int(),
  /** The stat a share is of (`SportDefinition.usage.share`). */
  share: z.string().min(1),
  /** The stat given a game beside it (`SportDefinition.usage.perGame`), or null. */
  perGame: z.string().min(1).nullable(),
  players: z.array(PlayerUsage),
});
export type UsageDocument = z.infer<typeof UsageDocument>;

/** Any document `GET /api/artifacts/:sport/:kind/:version` serves. */
export const ArtifactDocument = z.union([
  DirectoryDocument,
  WindowDocument,
  LinesDocument,
  HistoryDocument,
  ScheduleDocument,
  UsageDocument,
]);
export type ArtifactDocument = z.infer<typeof ArtifactDocument>;

/** The current version of one kind. */
export const ArtifactRef = z.object({
  kind: ArtifactKind,
  version: ArtifactVersion,
  builtAt: Iso,
  runId: z.string(),
  asOf: Iso.nullable(),
  /** The document's size as stored, in bytes of UTF-8. */
  bytes: z.number().int().min(0),
  /** Where it is served: `/api/artifacts/:sport/:kind/:version`. */
  path: z.string(),
});
export type ArtifactRef = z.infer<typeof ArtifactRef>;

/** `GET /api/sports/:sport/artifacts`: the current version of every kind built for the sport. */
export const ArtifactsView = z.object({
  sport: z.string(),
  /** Empty when nothing has been built yet, which is not a failure: the screens read the record then. */
  artifacts: z.array(ArtifactRef),
});
export type ArtifactsView = z.infer<typeof ArtifactsView>;

/**
 * A response header naming the artifact versions an answer was built from,
 * `sport/kind/version` joined by `, `. A league screen built from a version
 * says which, so a client can tell why two screens built a minute apart
 * disagree. Absent when the answer read the record instead.
 */
export const ARTIFACTS_HEADER = 'x-artifacts';

/** The path a version is served at. */
export const artifactPath = (sport: string, kind: string, version: string): string =>
  `/api/artifacts/${encodeURIComponent(sport)}/${encodeURIComponent(kind)}/${version}`;
